/**
 * Checklist Parser Utility
 *
 * Shared logic for parsing checklist data in both JSON and pipe-separated formats
 *
 * Primary responsibilities:
 * - Parse JSON format checklists (complex structure), including LLM-noisy JSON
 * - Recover items from corrupted / mashed JSON when JSON.parse fails
 * - Parse pipe-separated format checklists (simple structure)
 * - Provide consistent error handling
 * - Generate proper data structure for components
 */

export interface ChecklistItem {
  id: string
  text: string
  category?: string
  priority?: 'low' | 'medium' | 'high'
  checked?: boolean
}

export interface ChecklistData {
  title: string
  items: ChecklistItem[]
  className?: string
}

const PRIORITY_RE = /^(low|medium|high)$/i
const STRING_FIELD_RE = (field: string) =>
  new RegExp(`"${field}"\\s*:\\s*"((?:\\\\.|[^"\\\\])*)"`, 'g')

function unescapeJsonString(value: string): string {
  return value
    .replace(/\\"/g, '"')
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '\t')
    .replace(/\\\\/g, '\\')
}

function normalizePriority(
  value: unknown
): ChecklistItem['priority'] | undefined {
  if (typeof value !== 'string') return undefined
  const normalized = value.trim().toLowerCase()
  return PRIORITY_RE.test(normalized)
    ? (normalized as ChecklistItem['priority'])
    : undefined
}

function normalizeChecklistItem(item: unknown, index: number): ChecklistItem | null {
  if (item == null) return null

  if (typeof item === 'string') {
    const text = item.trim()
    if (!text) return null
    return {
      id: `item-${index}`,
      text,
      category: 'Tasks',
      checked: false
    }
  }

  if (typeof item !== 'object') {
    const text = String(item).trim()
    if (!text) return null
    return {
      id: `item-${index}`,
      text,
      category: 'Tasks',
      checked: false
    }
  }

  const record = item as Record<string, unknown>
  const textCandidate =
    record.text ?? record.label ?? record.title ?? record.name ?? record.content

  let text = ''
  if (typeof textCandidate === 'string') {
    text = textCandidate.trim()
  } else if (textCandidate != null && typeof textCandidate !== 'object') {
    text = String(textCandidate).trim()
  }

  // Never stringify the whole object into the checkbox label — that is the
  // "Checklist shows raw JSON" failure mode when JSON parse fell through.
  if (!text) return null

  return {
    id: typeof record.id === 'string' && record.id ? record.id : `item-${index}`,
    text,
    category: typeof record.category === 'string' ? record.category : undefined,
    priority: normalizePriority(record.priority),
    checked: Boolean(record.checked)
  }
}

function checklistFromJsonData(jsonData: unknown): ChecklistData {
  if (typeof jsonData === 'string') {
    // Double-encoded JSON blob
    return checklistFromJsonData(JSON.parse(jsonData))
  }

  if (!jsonData || typeof jsonData !== 'object') {
    throw new Error('Invalid JSON structure: expected object')
  }

  const record = jsonData as Record<string, unknown>
  const rawItems = record.items ?? record.tasks ?? record.todos

  if (!Array.isArray(rawItems)) {
    throw new Error('Invalid JSON structure: missing items array')
  }

  const items = rawItems
    .map((item, index) => normalizeChecklistItem(item, index))
    .filter((item): item is ChecklistItem => item != null)

  if (items.length === 0) {
    throw new Error('No valid checklist items found in JSON')
  }

  return {
    title: typeof record.title === 'string' && record.title ? record.title : 'Checklist',
    items,
    className: typeof record.className === 'string' ? record.className : undefined
  }
}

/** Strip fences / extract the outermost `{...}` so noisy LLM payloads still parse. */
export function extractChecklistJsonCandidate(rawContent: string): string {
  let text = rawContent.trim()

  // ```json ... ``` or ``` ... ```
  const fence = text.match(/^```(?:json|JSON)?\s*([\s\S]*?)```\s*$/)
  if (fence?.[1]) {
    text = fence[1].trim()
  }

  // Smart quotes → ASCII
  text = text.replace(/[\u201C\u201D]/g, '"').replace(/[\u2018\u2019]/g, "'")

  // Trailing commas before } or ]
  text = text.replace(/,\s*([}\]])/g, '$1')

  if (text.startsWith('{') && text.endsWith('}')) {
    return text
  }

  const start = text.indexOf('{')
  const end = text.lastIndexOf('}')
  if (start !== -1 && end > start) {
    return text.slice(start, end + 1)
  }

  return text
}

/**
 * Lightweight repair for common LLM mashups before JSON.parse:
 * - `"callpriority":"high"` → `"call","priority":"high"`
 * - empty `"category":""` kept valid
 * - mashed `"category":"Foo","priority":"high"},{"id":"f8"` fragments left to
 *   field recovery when still invalid
 */
export function repairChecklistJsonCandidate(raw: string): string {
  let text = raw

  // "...callpriority":"high" / "...callCategory":"..." style mash where the
  // priority/category key was glued onto the previous string value.
  text = text.replace(
    /([A-Za-z0-9 .,_/\-—–])(priority|category)"\s*:\s*"/gi,
    '$1","$2":"'
  )

  // `"category":"Rate-limit /":"f8","text":` → close category, start next item
  text = text.replace(
    /"category"\s*:\s*"([^"]*?)"\s*:\s*"([^"]+)"\s*,\s*"text"/gi,
    '"category":"$1"},{"id":"$2","text"'
  )

  // `"text":"...":"high"` missing priority key
  text = text.replace(
    /"text"\s*:\s*"((?:\\.|[^"\\])*)"\s*:\s*"(high|medium|low)"/gi,
    '"text":"$1","priority":"$2"'
  )

  // Trailing commas again after repairs
  text = text.replace(/,\s*([}\]])/g, '$1')

  return text
}

/**
 * Last-resort recovery: pull title + every `"text":"..."` item out of a broken
 * blob so a checklist still renders instead of vanishing.
 */
export function recoverChecklistFromBrokenJson(raw: string): ChecklistData | null {
  const titleMatch = STRING_FIELD_RE('title').exec(raw)
  const title = titleMatch?.[1]
    ? unescapeJsonString(titleMatch[1]).trim()
    : 'Checklist'

  const items: ChecklistItem[] = []
  const textRe = STRING_FIELD_RE('text')
  let match: RegExpExecArray | null
  let index = 0

  while ((match = textRe.exec(raw)) !== null) {
    const text = unescapeJsonString(match[1]).trim()
    if (!text) continue

    // Prefer fields that appear in the same object fragment *before* this text
    // value, so mashed neighbor items don't steal id/priority/category.
    const beforeStart = Math.max(0, match.index - 160)
    const before = raw.slice(beforeStart, match.index)
    const afterEnd = Math.min(raw.length, match.index + match[0].length + 100)
    const after = raw.slice(match.index + match[0].length, afterEnd)

    const idMatches = Array.from(before.matchAll(/"id"\s*:\s*"([^"]+)"/g))
    const id = idMatches.length > 0 ? idMatches[idMatches.length - 1][1] : `item-${index}`

    const priorityMatch =
      /"priority"\s*:\s*"(high|medium|low)"/i.exec(after) ||
      /"priority"\s*:\s*"(high|medium|low)"/i.exec(before)
    const categoryMatch =
      /"category"\s*:\s*"((?:\\.|[^"\\])*)"/.exec(before) ||
      /"category"\s*:\s*"((?:\\.|[^"\\])*)"/.exec(after)

    items.push({
      id,
      text,
      category: categoryMatch?.[1]
        ? unescapeJsonString(categoryMatch[1]).trim() || undefined
        : undefined,
      priority: normalizePriority(priorityMatch?.[1]),
      checked: false
    })
    index += 1
  }

  if (items.length === 0) return null

  return { title: title || 'Checklist', items }
}

/**
 * Parses checklist content from either JSON or pipe-separated format
 */
export function parseChecklistContent(rawContent: string): ChecklistData {
  const trimmedContent = rawContent.trim()
  const jsonCandidate = extractChecklistJsonCandidate(trimmedContent)

  // Prefer JSON whenever the payload looks like an object — never fall through
  // to pipe-split on a whole JSON blob (that yields one item = raw JSON text).
  if (jsonCandidate.startsWith('{')) {
    try {
      return checklistFromJsonData(JSON.parse(jsonCandidate))
    } catch {
      // Attempt common LLM repairs, then field-level recovery so the UI still
      // renders instead of swallowing the whole block. Prefer recovering from
      // the repaired string so glued id/priority keys are already split.
      const repaired = repairChecklistJsonCandidate(jsonCandidate)
      try {
        return checklistFromJsonData(JSON.parse(repaired))
      } catch {
        const recovered =
          recoverChecklistFromBrokenJson(repaired) ||
          recoverChecklistFromBrokenJson(jsonCandidate)
        if (recovered) return recovered
      }
    }
  }

  // Pipe-separated / plain list fallback — refuse to treat a JSON object blob
  // as a single pipe item.
  if (trimmedContent.startsWith('{') || jsonCandidate.startsWith('{')) {
    throw new Error('Failed to parse checklist JSON')
  }

  const items = trimmedContent
    .split('|')
    .map((item, index) => normalizeChecklistItem(item, index))
    .filter((item): item is ChecklistItem => item != null)

  if (items.length === 0) {
    throw new Error('No valid checklist items found')
  }

  return {
    title: 'Checklist',
    items
  }
}

/**
 * Converts checklist data to markdown format for text export
 */
export function checklistToMarkdown(data: ChecklistData): string {
  let checklistText = `📋 ${data.title}\n\n`

  if (data.items && Array.isArray(data.items)) {
    data.items.forEach((item: ChecklistItem) => {
      const status = item.checked ? '☑' : '☐'
      checklistText += `${status} ${item.text}\n`
    })
  }

  return checklistText + '\n'
}
